import { useCallback, useMemo } from 'react';
import type { VoidFunctionComponent } from 'react';

import { useAddToast } from '../../../../../../contexts/ToastContext';
import { window } from '../../../../../../globals';
import { useReportLogoSettings } from '../../../../../../hooks/api/organization/useReportLogoSettings';
import { useSBOMReportDefaultsSettings } from '../../../../../../hooks/api/organization/useSBOMReportDefaultsSettings';
import useUpdateReleaseGroupReportCustomText from '../../../../../../hooks/api/releaseGroups/useUpdateReleaseGroupReportCustomText';
import useReportOptions from '../../../../../../hooks/api/reports/useReportOptions';
import useEditable from '../../../../../../hooks/useEditable';
import { selectOptionFactory } from '../../../../../../utils';
import FormGroup from '../../../../../generic/Forms/FormGroup';
import LabeledSelect from '../../../../../generic/Forms/LabeledSelect';
import LabeledTextInput from '../../../../../generic/Forms/LabeledTextInput';
import { Description } from '../../../../../reportsv3/settings/Description';
import { Logo } from '../../../../../reportsv3/settings/Logo';
import SettingsPageHeader from '../../../../../shared/settings/SettingsPageHeader';
import Paragraph from '../../../../../shared/settings/SettingsPageParagraph';
import Section from '../../../../../shared/settings/SettingsPageSection';
import { useReleaseGroupsSettings } from '../context';
import type { SelectOption } from '../../../../../../types';

const ReleaseGroupsSettingsReports: VoidFunctionComponent = () => {
  const addToast = useAddToast();
  const {
    reportCustomText, projectGroupId, data: settings,
    isDirty, isSaving, onUpdate, onSave: onSaveSettings,
  } = useReleaseGroupsSettings();

  const {
    fileUrl,
    setFile,
    unsetFile,
    handleImgError,
    onSave: onSaveOrganizationLogo,
    isSaveDisabled: isOrganizationLogoSaveDisabled,
    isLoading: isOrganizationLogoLoading,
  } = useReportLogoSettings();

  const {
    data: descriptionText,
    onUpdate: onUpdateDescription,
    isDirty: isDescriptionDirty,
    onSave: onSaveDescriptionState,
  } = useEditable<string>(reportCustomText ?? '');

  const { mutateAsync: updateCustomText, isLoading: isCustomTextSaving } = useUpdateReleaseGroupReportCustomText();

  const {
    settings: sbomReportDefaults,
    placeholders: sbomReportPlaceholders,
    onUpdate: onUpdateSBOMReportDefaults,
    onSave: onSaveSBOMReportDefaults,
    isSaveDisabled: isSBOMReportDefaultsSaveDisabled,
    isSaving: isSBOMReportDefaultsSaving,
  } = useSBOMReportDefaultsSettings();

  const organizationId = window.user?.organizationId ?? 0;
  const { data: reportOptionsData, isLoading } = useReportOptions(organizationId);

  const reportOptionSelectOptions = useMemo(
    () => reportOptionsData?.results.map((option) => selectOptionFactory(option.id, option.name)) ?? [],
    [reportOptionsData],
  );

  const selectedLicensingReport = useMemo(
    () =>
      settings.defaultSavedOptionLicensingReport
        ? (reportOptionSelectOptions.find((opt) => opt.value === settings.defaultSavedOptionLicensingReport) ?? null)
        : null,
    [settings.defaultSavedOptionLicensingReport, reportOptionSelectOptions],
  );

  const selectedSbom = useMemo(
    () =>
      settings.defaultSavedOptionSbom
        ? (reportOptionSelectOptions.find((opt) => opt.value === settings.defaultSavedOptionSbom) ?? null)
        : null,
    [settings.defaultSavedOptionSbom, reportOptionSelectOptions],
  );

  const isAnySaving = isOrganizationLogoLoading || isSaving || isCustomTextSaving || isSBOMReportDefaultsSaving;
  const isSaveButtonDisabled =
    isOrganizationLogoSaveDisabled && !isDirty && !isDescriptionDirty && isSBOMReportDefaultsSaveDisabled;

  const onSaveAll = useCallback(async () => {
    if (!isOrganizationLogoSaveDisabled) {
      await onSaveOrganizationLogo();
    }
    if (isDescriptionDirty) {
      const releaseGroupId = Number(projectGroupId);
      if (Number.isNaN(releaseGroupId)) {
        addToast({ type: 'error', content: 'Failed to save report description: invalid release group' });
        return;
      }
      try {
        await updateCustomText({
          releaseGroupId,
          reportCustomText: descriptionText,
        });
        onSaveDescriptionState();
      } catch {
        // useToastedMutation already displays the error toast.
        // The catch prevents a description save failure from blocking the settings save below.
      }
    }
    if (!isSBOMReportDefaultsSaveDisabled) {
      await onSaveSBOMReportDefaults();
    }
    if (isDirty) {
      await onSaveSettings();
    }
  }, [
    addToast,
    isOrganizationLogoSaveDisabled,
    onSaveOrganizationLogo,
    isDescriptionDirty,
    updateCustomText,
    projectGroupId,
    descriptionText,
    onSaveDescriptionState,
    isSBOMReportDefaultsSaveDisabled,
    onSaveSBOMReportDefaults,
    isDirty,
    onSaveSettings,
  ]);

  return (
    <>
      <SettingsPageHeader heading="Reports">
        <SettingsPageHeader.SaveButton onClick={onSaveAll} isDisabled={isSaveButtonDisabled} isLoading={isAnySaving} />
      </SettingsPageHeader>
      <Logo fileUrl={fileUrl} setFile={setFile} unsetFile={unsetFile} handleImgError={handleImgError} />
      <Description value={descriptionText} onChange={onUpdateDescription} isDisabled={isAnySaving} isReleaseGroup />
      <Section>
        <Section.Left heading="Saved Options">
          <Section.Paragraph>Choose default options for each report type.</Section.Paragraph>
        </Section.Left>
        <Section.Right>
          <FormGroup>
            <LabeledSelect<SelectOption<number>>
              label="Default options for Licensing reports"
              id="release-group-settings-default-saved-option-licensing-report"
              placeholder="No filter selected"
              onChange={(option) => onUpdate({ defaultSavedOptionLicensingReport: option?.value ?? null })}
              options={reportOptionSelectOptions}
              value={selectedLicensingReport}
              isDisabled={isLoading || isSaving}
              isClearable
            />
            <Paragraph>This filter will be active by default when a user visits the report</Paragraph>
            <LabeledSelect<SelectOption<number>>
              label="Default options for SBOM reports"
              id="release-group-settings-default-saved-option-sbom"
              placeholder="No filter selected"
              onChange={(option) => onUpdate({ defaultSavedOptionSbom: option?.value ?? null })}
              options={reportOptionSelectOptions}
              value={selectedSbom}
              isDisabled={isLoading || isSaving}
              isClearable
            />
            <Paragraph>This filter will be active by default when a user visits the report</Paragraph>
          </FormGroup>
        </Section.Right>
      </Section>
      <Section>
        <Section.Left heading="SBOM Author Defaults">
          <Section.Paragraph>
            Set default author and supplier values for SBOM reports generated by users in your organization.
          </Section.Paragraph>
        </Section.Left>
        <Section.Right>
          <FormGroup>
            <LabeledTextInput
              label="Author Name"
              placeholder={sbomReportPlaceholders.sbomReportAuthorName}
              value={sbomReportDefaults.sbomReportAuthorName}
              onChange={(sbomReportAuthorName) => onUpdateSBOMReportDefaults({ sbomReportAuthorName })}
              size="small"
              isDisabled={isAnySaving}
            />
            <LabeledTextInput
              label="Author Email"
              placeholder={sbomReportPlaceholders.sbomReportAuthorEmail}
              value={sbomReportDefaults.sbomReportAuthorEmail}
              onChange={(sbomReportAuthorEmail) => onUpdateSBOMReportDefaults({ sbomReportAuthorEmail })}
              size="small"
              isDisabled={isAnySaving}
            />
            <LabeledTextInput
              label="Supplier"
              placeholder={sbomReportPlaceholders.sbomReportSupplier}
              value={sbomReportDefaults.sbomReportSupplier}
              onChange={(sbomReportSupplier) => onUpdateSBOMReportDefaults({ sbomReportSupplier })}
              size="small"
              isDisabled={isAnySaving}
            />
          </FormGroup>
        </Section.Right>
      </Section>
    </>
  );
};

export default ReleaseGroupsSettingsReports;
